import PropTypes from 'prop-types';
import React from 'react';
import ReactDom from 'react-dom';
import FieldComponent from '../vg-react-input-field/component';
import unpick from '../utilities/unpick';
import { allowedDomains } from '../application';
import { correctExpirationDateAutofill } from '../vg-react-input-field/utilities/autofill';
import trackEvent, { ANALYTICS_EVENTS } from '../utilities/analytics';
import { updatePayform } from '../vg-react-input-field/utilities/payform';
import ua from '../utilities/ua';

const getInitialFieldState = (defaultValue = '') => ({
  errorMessages: [],
  isDirty: false,
  isEmpty: true,
  isFocused: false,
  isValid: false,
  value: defaultValue,
  isTouched: false,
});

class Component extends React.Component {
  constructor(props) {
    super(props);
    this.onUpdate = this.onUpdate.bind(this);
    this.controllerRef = React.createRef();
    this.handleWindowMessages = this.handleWindowMessages.bind(this);
    this.state = {
      additionalProperties: {},
      fieldState: getInitialFieldState(props.defaultValue),
    };
  }

  handleWindowMessages(event) {
    const node = ReactDom.findDOMNode(this);
    switch (true) {
      case event.data === 'focus':
        this.handleFocus(node);
        break;
      case event.data === 'reset':
        this.handleReset();
        break;
      case event.data.autofill:
        this.handleAutofill(node, event.data.value);
        break;
      case event.data.messageName === 'setProperties':
        this.handleSetProperties(event.data);
        break;
      case event.data.messageName === 'setAllowedDomains':
        this.handleSetAllowedDomains(event.data);
        break;
      case event.data.messageName === 'setInputMask':
        this.handleSetInputMask(event.data);
        break;
      case event.data.messageName === 'setRegExpPattern':
        this.handleRegExpPattern(event.data);
        break;
      case event.data.messageName === 'changeCardType':
        this.handleCardTypeChange(event.data);
        break;
      case event.data.messageName === 'setCVCDependency':
        this.handleCVCDependency(event.data);
        break;
      case event.data === 'prefill':
        const { prefillValue } = this.props;
        if (prefillValue) {
          this.handleFieldPrefill(node, prefillValue);
        }
        break;
      case event.data.messageName === 'touch-fields':
        this.handleFieldsTouch();
        break;
    }
  }

  handleFocus = (node) => {
    if (node !== null) {
      node.focus();
    }
  };

  handleReset = () => {
    const { defaultValue } = this.props;
    this.setState({ fieldState: getInitialFieldState(defaultValue) }, () => {
      // TODO: combine this component and controller.js to a single component/hook.
      // this function call should be kept for getting validation errors.
      this.controllerRef.current.resetState(this.state.fieldState.value, 'reset');
    });
  };

  handleIframeFocus = (event) => {
    const node = ReactDom.findDOMNode(this);
    const isTab = event.type === 'keyup' && event.key === 'Tab';
    const isFocus = event.type === 'focus';
    const canFocus = node && typeof node.focus === 'function';
    const shouldFocus = (isTab || isFocus) && canFocus;

    if (shouldFocus) {
      node.focus();
      this.removeFocusListeners();
      window.addEventListener('blur', this.addFocusListeners);
    }
  };

  handleAutofill = (node, value) => {
    if (!node.value) {
      // specific logic for 'mm/yy' expiration date autofill
      if (
        node.getAttribute('autoComplete') === 'cc-exp' &&
        this.state.additionalProperties.yearLength === '2' &&
        value
      ) {
        value = correctExpirationDateAutofill(value);
      }
      node.value = value;
      node.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  handleSetProperties = (data) => {
    this.setState({ additionalProperties: data.additionalProperties });
  };

  handleSetAllowedDomains = (data) => {
    data.allowedDomains.forEach((domain) => {
      allowedDomains.push(domain);
    });
  };

  handleSetInputMask = (data) => {
    this.setState((prevState) => {
      let additionalProperties = Object.assign({}, prevState.additionalProperties, data.mask);
      return { additionalProperties };
    });
  };

  handleRegExpPattern = (data) => {
    this.setState((prevState) => {
      let additionalProperties = Object.assign({}, prevState.additionalProperties, {
        replacePattern: data.replacePattern,
      });
      return { additionalProperties };
    });
  };

  handleCardTypeChange = (data) => {
    if (window.cardType !== data.payload.cardType) {
      window.cardType = data.payload.cardType;
      updatePayform(data.payload.cards);
      this.setState((prevState) => {
        let additionalProperties = Object.assign({}, prevState.additionalProperties, {
          cardType: data.payload.cardType,
        });
        return { additionalProperties };
      });
    }
  };

  handleCVCDependency = (data) => {
    this.setState((prevState) => {
      let additionalProperties = Object.assign({}, prevState.additionalProperties, {
        cvcDependency: data.cvcDependency,
      });
      return { additionalProperties };
    });
  };

  handleFieldPrefill = (node, value) => {
    node.value = value;
    node.dispatchEvent(new Event('input', { bubbles: true }));
  };

  handleFieldsTouch = () => {
    if (!this.state.fieldState.isTouched) {
      this.controllerRef.current.resetState(this.state.fieldState.value, 'blur');
    }
  };

  componentDidMount() {
    window.addEventListener('message', this.handleWindowMessages, false);

    const defaultFeatures = ['defaultValue', 'autoComplete', 'showCardIcon', 'css', 'yearLength'];
    const features = defaultFeatures.filter((feature) => this.props[feature]);
    const getLatency = (d) => Date.now() - d;
    trackEvent({
      type: ANALYTICS_EVENTS.INIT,
      status: 'Ok',
      features,
      createdAt: window.globalConfig.createdAt,
      latency: getLatency(window.globalConfig.createdAt),
    });

    this.addFocusListeners();
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleWindowMessages);
    this.removeFocusListeners();
  }

  addFocusListeners = () => {
    const browser = ua.browser.name;
    if (browser === 'Firefox') {
      window.addEventListener('focus', this.handleIframeFocus);
    } else if (browser === 'Safari' && ua.isDesktop) {
      window.addEventListener('keyup', this.handleIframeFocus);
    }
  };

  removeFocusListeners = () => {
    window.removeEventListener('focus', this.handleIframeFocus);
    window.removeEventListener('keyup', this.handleIframeFocus);
  };

  onUpdate(fieldState) {
    this.setState({ fieldState }, () => {
      this.props.updateGlobalValue(this.props, fieldState);
    });
  }

  getStyle(isDirty, isValid) {
    const { errorColor, successColor, color } = this.props;
    const style = ['lineHeight', 'fontSize', 'fontFamily', 'color'].reduce((result, key) => {
      if (this.props[key] !== undefined) {
        result[key] = this.props[key];
      }
      return result;
    }, {});

    if (isDirty) {
      const v = (isValid ? successColor : errorColor) || color;
      if (v !== undefined) {
        style.color = v;
      }
    }
    return style;
  }

  render() {
    if (this.props.type) {
      const props = unpick(this.props, [
        'fieldId',
        'updateGlobalValue',
        'errorColor',
        'successColor',
        'lineHeight',
        'fontSize',
        'fontFamily',
        'color',
        'serializers',
        'defaultValue',
        'value',
        'vgsCollectSessionId',
        'createdAt',
        'prefillValue',
      ]);

      if (['checkbox', 'radio'].includes(this.props.type)) {
        const { value: valueWhenChecked = 'true' } = this.props;
        props.valueWhenChecked = valueWhenChecked;
      }

      const { fieldState, additionalProperties } = this.state;

      const style = this.getStyle(fieldState.isDirty, fieldState.isValid);
      return (
        <FieldComponent
          ref={this.controllerRef}
          style={style}
          {...props}
          {...additionalProperties}
          fieldState={fieldState}
          defaultValue={this.props.defaultValue}
          onUpdate={this.onUpdate}
        />
      );
    }
    return <span />;
  }
}

Component.displayName = 'FieldController';
Component.propTypes = {
  updateGlobalValue: PropTypes.func,
  name: PropTypes.string,
  type: PropTypes.string,
  formId: PropTypes.string,
  validations: PropTypes.object,
  serializers: PropTypes.array,
};

export default Component;
